import { useCallback, useState } from 'react';

/** Immutably move an array element from one index to another. */
export const arrayMove = (list, from, to) => {
  if (from === to || from == null || to == null) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Native HTML5 drag-and-drop reordering for a vertical list.
 *
 * Drag is a pointer-only affordance, so every consumer also renders
 * move-up / move-down buttons that call `move()` directly — that keeps the
 * feature usable on touch devices and with a keyboard.
 */
export const useSortable = (items, onReorder, { disabled = false } = {}) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const reset = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
  }, []);

  const move = useCallback(
    (from, to) => {
      if (disabled) return;
      if (to < 0 || to >= items.length || from === to) return;
      onReorder(arrayMove(items, from, to), from, to);
    },
    [items, onReorder, disabled]
  );

  const getItemProps = useCallback(
    (index) => {
      if (disabled) return {};
      return {
        draggable: true,
        onDragStart: (e) => {
          setDragIndex(index);
          e.dataTransfer.effectAllowed = 'move';
          // Firefox refuses to start a drag without payload
          try {
            e.dataTransfer.setData('text/plain', String(index));
          } catch {
            /* ignore — some browsers lock the data store */
          }
        },
        onDragOver: (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (index !== overIndex) setOverIndex(index);
        },
        onDragLeave: () => {
          if (index === overIndex) setOverIndex(null);
        },
        onDrop: (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (dragIndex !== null) move(dragIndex, index);
          reset();
        },
        onDragEnd: reset,
      };
    },
    [disabled, dragIndex, overIndex, move, reset]
  );

  /** Class suffix so consumers can style the dragged row and the drop target. */
  const getItemClass = useCallback(
    (index) => {
      const classes = [];
      if (dragIndex === index) classes.push('is-dragging');
      if (overIndex === index && dragIndex !== null && dragIndex !== index) {
        classes.push(dragIndex < index ? 'is-over-below' : 'is-over-above');
      }
      return classes.join(' ');
    },
    [dragIndex, overIndex]
  );

  return { dragIndex, overIndex, move, getItemProps, getItemClass };
};

export default useSortable;
